import React, { FC, useMemo, useState, useCallback } from "react"
import { AutoComplete } from "antd"
import { getGameOptions } from "./gameOptions"

const allGameOptions = getGameOptions()

interface GameAutoCompleteProps {
  value?: string
  onChange?: (value: string) => void
  onApply?: (value: string, gameId: string | null) => void
  onClear?: () => void
  placeholder?: string
  style?: React.CSSProperties
  allowClear?: boolean
  applyOnBlur?: boolean
}

const extractGameId = (val: string): string | null => {
  if (!val) return null

  return val.includes(" - ") ? val.split(" - ")[0] : val.trim()
}

export const GameAutoComplete: FC<GameAutoCompleteProps> = ({
  value: controlledValue,
  onChange,
  onApply,
  onClear,
  placeholder = "Select a game or enter game ID",
  style = { width: 300 },
  allowClear = true,
  applyOnBlur = false,
}) => {
  const [internalValue, setInternalValue] = useState("")
  const value = controlledValue ?? internalValue
  const isControlled = controlledValue !== undefined

  const filteredGameOptions = useMemo(() => {
    if (!value) return allGameOptions

    const searchValue = value.toLowerCase()

    return allGameOptions.filter(option => option.label.toLowerCase().includes(searchValue)
      || option.value.toLowerCase().includes(searchValue))
  }, [value])

  const applyFilter = useCallback((val: string) => {
    if (onApply) {
      onApply(val, extractGameId(val))
    }
  }, [onApply])

  const handleChange = useCallback((val: string) => {
    if (!isControlled) {
      setInternalValue(val)
    }

    if (!val && onClear) {
      onClear()
    }

    if (onChange) {
      onChange(val)
    }
  }, [isControlled, onChange, onClear])

  const handleSelect = useCallback((val: string) => {
    if (!isControlled) {
      setInternalValue(val)
    }

    if (onChange) {
      onChange(val)
    }

    applyFilter(val)
  }, [isControlled, onChange, applyFilter])

  const handleBlur = useCallback(() => {
    if (applyOnBlur && value) {
      applyFilter(value)
    }
  }, [applyOnBlur, value, applyFilter])

  return (
    <AutoComplete
      placeholder={placeholder}
      style={style}
      allowClear={allowClear}
      value={value}
      onChange={handleChange}
      onSelect={handleSelect}
      onBlur={handleBlur}
      options={filteredGameOptions}
      filterOption={false}
    />
  )
}
